import { c as _c } from "react/compiler-runtime";
// Лента заявки: касания, задачи, файлы и смены стадий одним списком, новые
// сверху. Свои касания можно поправить или удалить (администратор — любые).
import { CRM_ACTIVITY_KIND_LABELS, CRM_ACTIVITY_OUTCOMES, CRM_ACTIVITY_OUTCOME_LABELS, CRM_FILE_CATEGORY_LABELS, CRM_TASK_KIND_LABELS, type CrmActivity, type CrmActivityOutcome, type CrmFile, type CrmTimelineItem } from "@osmix/contracts";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Bot, CheckCircle2, CircleDot, FilePen, History, ListPlus, Paperclip, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { APPLICATION_STATUSES, type ApplicationStatus } from "@/api";
import { useDeleteActivity, useUpdateActivity } from "@/api/hooks/use-application-crm";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Segmented } from "@/components/segmented";
import { cn } from "@/lib/utils";
import { ACTIVITY_ICON } from "./activity-composer";
import { FileIcon } from "./files-panel";
import { errorText, formatDayTime } from "./format";
import { Chip, FieldError, Panel, Spinner, STATUS_ACCENT, Textarea } from "./ui";
type Filter = "all" | "activity" | "task" | "file" | "status";
const FILTERS: {
  value: Filter;
  label: string;
}[] = [{
  value: "all",
  label: "Всё"
}, {
  value: "activity",
  label: "Касания"
}, {
  value: "task",
  label: "Задачи"
}, {
  value: "file",
  label: "Файлы"
}, {
  value: "status",
  label: "Стадии"
}];
const OUTCOME_TONE: Record<CrmActivityOutcome, string> = {
  reached: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  interested: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  thinking: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-500",
  callback: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-500",
  no_answer: "border-border text-muted-foreground",
  not_interested: "border-destructive/40 bg-destructive/10 text-destructive"
};
const PAGE = 30;

/**
 * Запись `status` с `fromStatus === toStatus` — служебная строка журнала
 * (правка телефона, назначение, DaData), а не смена стадии.
 */
const isStageChange = (item: CrmTimelineItem) => item.type === "status" && !!item.toStatus && item.fromStatus !== item.toStatus;
const keyOf = (item: CrmTimelineItem, index: number) => {
  if (item.type === "activity") return `a:${item.activity.id}`;
  if (item.type === "task") return `t:${item.task.id}:${item.action}`;
  if (item.type === "file") return `f:${item.file.id}`;
  return `s:${item.at}:${index}`;
};
export function Timeline(t0) {
  const $ = _c(38);
  const {
    applicationId,
    items,
    files,
    currentUserId,
    isAdmin,
    canManage,
    onOpenFile
  } = t0;
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(PAGE);
  let T0;
  let shown;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let visible;
  if ($[0] !== applicationId || $[1] !== canManage || $[2] !== currentUserId || $[3] !== files || $[4] !== filter || $[5] !== isAdmin || $[6] !== items || $[7] !== limit || $[8] !== onOpenFile) {
    visible = filter === "all" ? items : items.filter(item => item.type === filter && (filter !== "status" || isStageChange(item)));
    shown = visible.slice(0, limit);
    T0 = Panel;
    t4 = "\u041B\u0435\u043D\u0442\u0430";
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <History className="size-4 text-muted-foreground" />;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    t6 = items.length;
    t7 = "crm-timeline";
    t1 = "flex flex-col gap-3";
    let t8;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = next => {
        setFilter(next);
        setLimit(PAGE);
      };
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    if ($[21] !== filter) {
      t2 = <Segmented value={filter} onChange={t8} options={FILTERS} className="self-start" />;
      $[21] = filter;
      $[22] = t2;
    } else {
      t2 = $[22];
    }
    t3 = shown.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">{filter === "all" ? "\u041F\u043E\u043A\u0430 \u043F\u0443\u0441\u0442\u043E \u2014 \u0437\u0430\u043F\u0438\u0448\u0438\u0442\u0435 \u043F\u0435\u0440\u0432\u043E\u0435 \u043A\u0430\u0441\u0430\u043D\u0438\u0435." : "\u0417\u0434\u0435\u0441\u044C \u043F\u043E\u043A\u0430 \u043D\u0438\u0447\u0435\u0433\u043E \u043D\u0435\u0442."}</p> : <ol className="relative flex flex-col">{shown.map((item_0, index) => <li key={keyOf(item_0, index)} className="relative flex gap-3 pb-4 last:pb-0">{index < shown.length - 1 && <span aria-hidden={true} className="absolute top-8 bottom-0 left-[15px] w-px bg-border" />}<TimelineEntry applicationId={applicationId} item={item_0} files={files} editable={canManage && item_0.type === "activity" && (isAdmin || !!currentUserId && item_0.activity.author?.id === currentUserId)} onOpenFile={onOpenFile} /></li>)}</ol>;
    $[0] = applicationId;
    $[1] = canManage;
    $[2] = currentUserId;
    $[3] = files;
    $[4] = filter;
    $[5] = isAdmin;
    $[6] = items;
    $[7] = limit;
    $[8] = onOpenFile;
    $[9] = T0;
    $[10] = shown;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = visible;
  } else {
    T0 = $[9];
    shown = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    visible = $[18];
  }
  let t8;
  if ($[23] !== shown.length || $[24] !== visible.length) {
    t8 = visible.length > shown.length && <Button variant="outline" size="sm" className="self-center" onClick={() => setLimit(_temp)}>Показать ещё {Math.min(PAGE, visible.length - shown.length)}</Button>;
    $[23] = shown.length;
    $[24] = visible.length;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t1 || $[27] !== t2 || $[28] !== t3 || $[29] !== t8) {
    t9 = <div className={t1}>{t2}{t3}{t8}</div>;
    $[26] = t1;
    $[27] = t2;
    $[28] = t3;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== T0 || $[32] !== t4 || $[33] !== t5 || $[34] !== t6 || $[35] !== t7 || $[36] !== t9) {
    t10 = <T0 title={t4} icon={t5} count={t6} data-testid={t7}>{t9}</T0>;
    $[31] = T0;
    $[32] = t4;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = t9;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  return t10;
}
function _temp(n) {
  return n + PAGE;
}
function Dot(t0) {
  const $ = _c(5);
  const {
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border bg-background text-muted-foreground", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <span className={t1}>{children}</span>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function Meta(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="text-xs text-muted-foreground">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function TimelineEntry(t0) {
  const $ = _c(98);
  const {
    applicationId,
    item,
    files,
    editable,
    onOpenFile
  } = t0;
  const {
    t
  } = useTranslation();
  if (item.type === "activity") {
    let t1;
    if ($[0] !== applicationId || $[1] !== editable || $[2] !== files || $[3] !== item.activity || $[4] !== onOpenFile) {
      t1 = <ActivityEntry applicationId={applicationId} activity={item.activity} files={files} editable={editable} onOpenFile={onOpenFile} />;
      $[0] = applicationId;
      $[1] = editable;
      $[2] = files;
      $[3] = item.activity;
      $[4] = onOpenFile;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  if (item.type === "status") {
    let t1;
    if ($[6] !== item) {
      t1 = isStageChange(item);
      $[6] = item;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    const stage = t1;
    const known = stage && APPLICATION_STATUSES.includes(item.toStatus as ApplicationStatus) ? item.toStatus as ApplicationStatus : null;
    let t2;
    if ($[8] !== item.automated || $[9] !== stage) {
      t2 = <Dot>{item.automated ? <Bot className="size-4 text-primary" /> : stage ? <CircleDot className="size-4" /> : <FilePen className="size-4" />}</Dot>;
      $[8] = item.automated;
      $[9] = stage;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    const t3 = !stage && "text-muted-foreground";
    let t4;
    if ($[11] !== t3) {
      t4 = cn("text-sm break-words", t3);
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== item.message || $[14] !== t4) {
      t5 = <p className={t4}>{item.message}</p>;
      $[13] = item.message;
      $[14] = t4;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== known || $[17] !== t) {
      t6 = known && <span className="inline-flex items-center gap-1.5 text-xs"><span className={cn("size-2 rounded-full", STATUS_ACCENT[known])} />{t(`applications.status.${known}`)}</span>;
      $[16] = known;
      $[17] = t;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== item.actor) {
      t7 = item.actor && <Meta>{item.actor.name}</Meta>;
      $[19] = item.actor;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== item.at) {
      t8 = formatDayTime(item.at);
      $[21] = item.at;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== t8) {
      t9 = <Meta>{t8}</Meta>;
      $[23] = t8;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== t6 || $[26] !== t7 || $[27] !== t9) {
      t10 = <p className="flex flex-wrap items-center gap-x-2 gap-y-1">{t6}{t7}{t9}</p>;
      $[25] = t6;
      $[26] = t7;
      $[27] = t9;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    let t11;
    if ($[29] !== t10 || $[30] !== t5) {
      t11 = <div className="min-w-0 flex-1 pt-1">{t5}{t10}</div>;
      $[29] = t10;
      $[30] = t5;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    let t12;
    if ($[32] !== t11 || $[33] !== t2) {
      t12 = <>{t2}{t11}</>;
      $[32] = t11;
      $[33] = t2;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    return t12;
  }
  if (item.type === "task") {
    const done = item.action === "done";
    const t1 = done ? "text-emerald-600" : undefined;
    let t2;
    if ($[35] !== done) {
      t2 = done ? <CheckCircle2 className="size-4" /> : <ListPlus className="size-4" />;
      $[35] = done;
      $[36] = t2;
    } else {
      t2 = $[36];
    }
    let t3;
    if ($[37] !== t1 || $[38] !== t2) {
      t3 = <Dot className={t1}>{t2}</Dot>;
      $[37] = t1;
      $[38] = t2;
      $[39] = t3;
    } else {
      t3 = $[39];
    }
    const t4 = done ? "\u0412\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u0430 \u0437\u0430\u0434\u0430\u0447\u0430" : "\u041F\u043E\u0441\u0442\u0430\u0432\u043B\u0435\u043D\u0430 \u0437\u0430\u0434\u0430\u0447\u0430";
    let t5;
    if ($[40] !== t4) {
      t5 = <span className="text-muted-foreground">{t4}</span>;
      $[40] = t4;
      $[41] = t5;
    } else {
      t5 = $[41];
    }
    let t6;
    if ($[42] !== item.task.title) {
      t6 = <span className="font-medium">«{item.task.title}»</span>;
      $[42] = item.task.title;
      $[43] = t6;
    } else {
      t6 = $[43];
    }
    let t7;
    if ($[44] !== t5 || $[45] !== t6) {
      t7 = <p className="text-sm break-words">{t5}{" "}{t6}</p>;
      $[44] = t5;
      $[45] = t6;
      $[46] = t7;
    } else {
      t7 = $[46];
    }
    const t8 = CRM_TASK_KIND_LABELS[item.task.kind];
    let t9;
    if ($[47] !== t8) {
      t9 = <Meta>{t8}</Meta>;
      $[47] = t8;
      $[48] = t9;
    } else {
      t9 = $[48];
    }
    let t10;
    if ($[49] !== done || $[50] !== item.task.dueAt) {
      t10 = !done && item.task.dueAt && <Meta>срок {formatDayTime(item.task.dueAt)}</Meta>;
      $[49] = done;
      $[50] = item.task.dueAt;
      $[51] = t10;
    } else {
      t10 = $[51];
    }
    let t11;
    if ($[52] !== done || $[53] !== item.task.createdBy || $[54] !== item.task.doneBy) {
      t11 = (done ? item.task.doneBy : item.task.createdBy) && <Meta>{(done ? item.task.doneBy : item.task.createdBy)?.name}</Meta>;
      $[52] = done;
      $[53] = item.task.createdBy;
      $[54] = item.task.doneBy;
      $[55] = t11;
    } else {
      t11 = $[55];
    }
    let t12;
    if ($[56] !== item.at) {
      t12 = formatDayTime(item.at);
      $[56] = item.at;
      $[57] = t12;
    } else {
      t12 = $[57];
    }
    let t13;
    if ($[58] !== t12) {
      t13 = <Meta>{t12}</Meta>;
      $[58] = t12;
      $[59] = t13;
    } else {
      t13 = $[59];
    }
    let t14;
    if ($[60] !== t10 || $[61] !== t11 || $[62] !== t13 || $[63] !== t9) {
      t14 = <p className="flex flex-wrap gap-x-2">{t9}{t10}{t11}{t13}</p>;
      $[60] = t10;
      $[61] = t11;
      $[62] = t13;
      $[63] = t9;
      $[64] = t14;
    } else {
      t14 = $[64];
    }
    let t15;
    if ($[65] !== t14 || $[66] !== t7) {
      t15 = <div className="min-w-0 flex-1 pt-1">{t7}{t14}</div>;
      $[65] = t14;
      $[66] = t7;
      $[67] = t15;
    } else {
      t15 = $[67];
    }
    let t16;
    if ($[68] !== t15 || $[69] !== t3) {
      t16 = <>{t3}{t15}</>;
      $[68] = t15;
      $[69] = t3;
      $[70] = t16;
    } else {
      t16 = $[70];
    }
    return t16;
  }
  let t1;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Dot><Paperclip className="size-4" /></Dot>;
    $[71] = t1;
  } else {
    t1 = $[71];
  }
  let t2;
  if ($[72] !== item.file || $[73] !== onOpenFile) {
    t2 = () => onOpenFile(item.file);
    $[72] = item.file;
    $[73] = onOpenFile;
    $[74] = t2;
  } else {
    t2 = $[74];
  }
  let t3;
  if ($[75] !== item.file) {
    t3 = <FileIcon file={item.file} />;
    $[75] = item.file;
    $[76] = t3;
  } else {
    t3 = $[76];
  }
  let t4;
  if ($[77] !== item.file.filename) {
    t4 = <span className="truncate">{item.file.filename}</span>;
    $[77] = item.file.filename;
    $[78] = t4;
  } else {
    t4 = $[78];
  }
  let t5;
  if ($[79] !== t2 || $[80] !== t3 || $[81] !== t4) {
    t5 = <button type="button" onClick={t2} className="flex max-w-full items-center gap-1.5 text-left text-sm hover:underline">{t3}{t4}</button>;
    $[79] = t2;
    $[80] = t3;
    $[81] = t4;
    $[82] = t5;
  } else {
    t5 = $[82];
  }
  const t6 = CRM_FILE_CATEGORY_LABELS[item.file.category];
  let t7;
  if ($[83] !== t6) {
    t7 = <Meta>{t6}</Meta>;
    $[83] = t6;
    $[84] = t7;
  } else {
    t7 = $[84];
  }
  let t8;
  if ($[85] !== item.file.uploadedBy) {
    t8 = item.file.uploadedBy && <Meta>{item.file.uploadedBy.name}</Meta>;
    $[85] = item.file.uploadedBy;
    $[86] = t8;
  } else {
    t8 = $[86];
  }
  let t9;
  if ($[87] !== item.at) {
    t9 = formatDayTime(item.at);
    $[87] = item.at;
    $[88] = t9;
  } else {
    t9 = $[88];
  }
  let t10;
  if ($[89] !== t9) {
    t10 = <Meta>{t9}</Meta>;
    $[89] = t9;
    $[90] = t10;
  } else {
    t10 = $[90];
  }
  let t11;
  if ($[91] !== t10 || $[92] !== t7 || $[93] !== t8) {
    t11 = <p className="flex flex-wrap gap-x-2">{t7}{t8}{t10}</p>;
    $[91] = t10;
    $[92] = t7;
    $[93] = t8;
    $[94] = t11;
  } else {
    t11 = $[94];
  }
  let t12;
  if ($[95] !== t11 || $[96] !== t5) {
    t12 = <>{t1}<div className="min-w-0 flex-1 pt-1">{t5}{t11}</div></>;
    $[95] = t11;
    $[96] = t5;
    $[97] = t12;
  } else {
    t12 = $[97];
  }
  return t12;
}
function ActivityEntry(t0) {
  const $ = _c(83);
  const {
    applicationId,
    activity,
    files,
    editable,
    onOpenFile
  } = t0;
  const update = useUpdateActivity(applicationId);
  const remove = useDeleteActivity(applicationId);
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(activity.body);
  const [outcome, setOutcome] = useState(activity.outcome);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState(null);
  const Icon = ACTIVITY_ICON[activity.kind];
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== Icon || $[1] !== activity.author || $[2] !== activity.body || $[3] !== activity.editedAt || $[4] !== activity.fileIds || $[5] !== activity.id || $[6] !== activity.kind || $[7] !== activity.occurredAt || $[8] !== activity.outcome || $[9] !== body || $[10] !== editable || $[11] !== editing || $[12] !== error || $[13] !== files || $[14] !== onOpenFile || $[15] !== outcome || $[16] !== update) {
    let t6;
    if ($[22] !== files) {
      t6 = id => files.find(file => file.id === id);
      $[22] = files;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    const attached = activity.fileIds.map(t6).filter(_temp2);
    const withOutcome = activity.kind === "call" || activity.kind === "meeting";
    let t7;
    if ($[24] !== activity.id || $[25] !== body || $[26] !== outcome || $[27] !== update) {
      t7 = async () => {
        setError(null);
        ;
        try {
          await update.mutateAsync({
            activityId: activity.id,
            patch: {
              body: body.trim(),
              outcome
            }
          });
          setEditing(false);
        } catch (t8) {
          const e = t8;
          setError(errorText(e));
        }
      };
      $[24] = activity.id;
      $[25] = body;
      $[26] = outcome;
      $[27] = update;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    const save = t7;
    if ($[29] !== Icon) {
      t5 = <Dot className="text-foreground"><Icon className="size-4" /></Dot>;
      $[29] = Icon;
      $[30] = t5;
    } else {
      t5 = $[30];
    }
    t1 = "group min-w-0 flex-1 pt-1";
    const t8 = CRM_ACTIVITY_KIND_LABELS[activity.kind];
    let t9;
    if ($[31] !== t8) {
      t9 = <span className="text-sm font-medium">{t8}</span>;
      $[31] = t8;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    let t10;
    if ($[33] !== activity.outcome) {
      t10 = activity.outcome && <Badge variant="outline" className={cn("font-normal", OUTCOME_TONE[activity.outcome])}>{CRM_ACTIVITY_OUTCOME_LABELS[activity.outcome]}</Badge>;
      $[33] = activity.outcome;
      $[34] = t10;
    } else {
      t10 = $[34];
    }
    let t11;
    if ($[35] !== activity.author) {
      t11 = activity.author && <Meta>{activity.author.name}</Meta>;
      $[35] = activity.author;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    let t12;
    if ($[37] !== activity.occurredAt) {
      t12 = formatDayTime(activity.occurredAt);
      $[37] = activity.occurredAt;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    const t13 = activity.editedAt && " \xB7 \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u043E";
    let t14;
    if ($[39] !== t12 || $[40] !== t13) {
      t14 = <Meta>{t12}{t13}</Meta>;
      $[39] = t12;
      $[40] = t13;
      $[41] = t14;
    } else {
      t14 = $[41];
    }
    let t15;
    if ($[42] !== activity.body || $[43] !== activity.outcome || $[44] !== editable || $[45] !== editing) {
      t15 = editable && !editing && <span className="ml-auto flex gap-0.5 md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:focus-within:opacity-100"><Button size="icon-xs" variant="ghost" aria-label={"\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C \u043A\u0430\u0441\u0430\u043D\u0438\u0435"} onClick={() => {
          setBody(activity.body);
          setOutcome(activity.outcome);
          setEditing(true);
        }}><Pencil /></Button><Button size="icon-xs" variant="ghost" aria-label={"\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u043A\u0430\u0441\u0430\u043D\u0438\u0435"} className="hover:text-destructive" onClick={() => setConfirm(true)}><Trash2 /></Button></span>;
      $[42] = activity.body;
      $[43] = activity.outcome;
      $[44] = editable;
      $[45] = editing;
      $[46] = t15;
    } else {
      t15 = $[46];
    }
    if ($[47] !== t10 || $[48] !== t11 || $[49] !== t14 || $[50] !== t15 || $[51] !== t9) {
      t2 = <div className="flex flex-wrap items-center gap-x-2 gap-y-1">{t9}{t10}{t11}{t14}{t15}</div>;
      $[47] = t10;
      $[48] = t11;
      $[49] = t14;
      $[50] = t15;
      $[51] = t9;
      $[52] = t2;
    } else {
      t2 = $[52];
    }
    if ($[53] !== activity.body || $[54] !== body || $[55] !== editing || $[56] !== error || $[57] !== outcome || $[58] !== save || $[59] !== update || $[60] !== withOutcome) {
      t3 = editing ? <div className="mt-2 flex flex-col gap-2"><Textarea rows={3} autoFocus={true} value={body} aria-label={"\u0422\u0435\u043A\u0441\u0442 \u043A\u0430\u0441\u0430\u043D\u0438\u044F"} disabled={update.isPending} onChange={e_0 => setBody(e_0.target.value)} onKeyDown={event => {
          if (event.key === "Escape") {
            setEditing(false);
          }
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            save();
          }
        }} />{withOutcome && <div className="flex flex-wrap gap-1.5">{CRM_ACTIVITY_OUTCOMES.map(value => <Chip key={value} active={outcome === value} className="px-2.5 text-xs" onClick={() => setOutcome(outcome === value ? null : value)}>{CRM_ACTIVITY_OUTCOME_LABELS[value]}</Chip>)}</div>}<FieldError>{error}</FieldError><div className="flex gap-2"><Button size="sm" disabled={update.isPending} onClick={() => void save()}>{update.isPending && <Spinner />}Сохранить</Button><Button size="sm" variant="ghost" disabled={update.isPending} onClick={() => setEditing(false)}>Отмена</Button></div></div> : activity.body && <p className="mt-1 text-sm break-words whitespace-pre-line">{activity.body}</p>;
      $[53] = activity.body;
      $[54] = body;
      $[55] = editing;
      $[56] = error;
      $[57] = outcome;
      $[58] = save;
      $[59] = update;
      $[60] = withOutcome;
      $[61] = t3;
    } else {
      t3 = $[61];
    }
    t4 = attached.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{attached.map(file_1 => <button key={file_1.id} type="button" onClick={() => onOpenFile(file_1)} className="inline-flex max-w-full items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 py-0.5 text-xs transition-colors hover:bg-muted"><FileIcon file={file_1} className="size-3.5" /><span className="max-w-48 truncate">{file_1.filename}</span></button>)}</div>;
    $[0] = Icon;
    $[1] = activity.author;
    $[2] = activity.body;
    $[3] = activity.editedAt;
    $[4] = activity.fileIds;
    $[5] = activity.id;
    $[6] = activity.kind;
    $[7] = activity.occurredAt;
    $[8] = activity.outcome;
    $[9] = body;
    $[10] = editable;
    $[11] = editing;
    $[12] = error;
    $[13] = files;
    $[14] = onOpenFile;
    $[15] = outcome;
    $[16] = update;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
  } else {
    t1 = $[17];
    t2 = $[18];
    t3 = $[19];
    t4 = $[20];
    t5 = $[21];
  }
  let t6;
  if ($[62] !== editing || $[63] !== error) {
    t6 = !editing && <FieldError>{error}</FieldError>;
    $[62] = editing;
    $[63] = error;
    $[64] = t6;
  } else {
    t6 = $[64];
  }
  let t7;
  if ($[65] !== t1 || $[66] !== t2 || $[67] !== t3 || $[68] !== t4 || $[69] !== t6) {
    t7 = <div className={t1}>{t2}{t3}{t4}{t6}</div>;
    $[65] = t1;
    $[66] = t2;
    $[67] = t3;
    $[68] = t4;
    $[69] = t6;
    $[70] = t7;
  } else {
    t7 = $[70];
  }
  let t8;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setConfirm(false);
    $[71] = t8;
  } else {
    t8 = $[71];
  }
  let t9;
  if ($[72] !== activity.id || $[73] !== remove) {
    t9 = () => remove.mutate(activity.id, {
      onSuccess: () => setConfirm(false),
      onError: e_1 => {
        setConfirm(false);
        setError(errorText(e_1));
      }
    });
    $[72] = activity.id;
    $[73] = remove;
    $[74] = t9;
  } else {
    t9 = $[74];
  }
  let t10;
  if ($[75] !== confirm || $[76] !== remove.isPending || $[77] !== t9) {
    t10 = <ConfirmDialog open={confirm} title={"\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u043A\u0430\u0441\u0430\u043D\u0438\u0435?"} description={"\u0417\u0430\u043F\u0438\u0441\u044C \u043F\u0440\u043E\u043F\u0430\u0434\u0451\u0442 \u0438\u0437 \u043B\u0435\u043D\u0442\u044B. \u041F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u043D\u044B\u0435 \u0444\u0430\u0439\u043B\u044B \u043E\u0441\u0442\u0430\u043D\u0443\u0442\u0441\u044F \u0432 \u043A\u0430\u0440\u0442\u043E\u0447\u043A\u0435."} confirmLabel={"\u0423\u0434\u0430\u043B\u0438\u0442\u044C"} confirmVariant="destructive" busy={remove.isPending} onClose={t8} onConfirm={t9} />;
    $[75] = confirm;
    $[76] = remove.isPending;
    $[77] = t9;
    $[78] = t10;
  } else {
    t10 = $[78];
  }
  let t11;
  if ($[79] !== t10 || $[80] !== t5 || $[81] !== t7) {
    t11 = <>{t5}{t7}{t10}</>;
    $[79] = t10;
    $[80] = t5;
    $[81] = t7;
    $[82] = t11;
  } else {
    t11 = $[82];
  }
  return t11;
}
function _temp2(file_0) {
  return !!file_0;
}